import axios from 'axios';

export const API_BASE_URL = 'https://pos.akiliapp.co.tz';

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  try {
    const raw = localStorage.getItem('pos_session');
    if (raw) {
      const session = JSON.parse(raw);
      const token = session?.accessToken;
      if (token) {
        config.headers = config.headers ?? {};
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
  } catch {
    // ignore invalid session storage
  }
  return config;
});

/**
 * Laravel-style payloads sometimes return `errorMessage` as a string[].
 * Normalizes in-place so UI code can always use a string.
 * Example: { status: "ERROR", errorMessage: ["The product id field is required."] }
 */
function normalizeErrorMessageInData(data) {
  if (!data || typeof data !== 'object') return;
  const em = data.errorMessage;
  if (Array.isArray(em)) {
    data.errorMessage = em
      .filter((x) => x != null && String(x).trim() !== '')
      .map(String)
      .join(' ');
  }
}

api.interceptors.response.use(
  (response) => {
    normalizeErrorMessageInData(response?.data);
    return response;
  },
  (error) => {
    normalizeErrorMessageInData(error?.response?.data);
    return Promise.reject(error);
  }
);

export const authApi = {
  login: (email, password) =>
    api.post('/api/auth/login', { email, password }),
};

export const productCategoryApi = {
  /** GET /api/productCategory/ilist — paginated list; optional search `q` */
  ilist: ({ paginate = true, per_page = 5, page = 1, q } = {}) =>
    api.get('/api/productCategory/ilist', {
      params: {
        paginate,
        per_page,
        page,
        ...(q != null && String(q).trim() !== '' ? { q: String(q).trim() } : {}),
      },
    }),

  save: ({ id, uuid, name, description }) =>
    api.post('/api/productCategory/iformAction', {
      form_method: 'save',
      ...(id ? { id } : null),
      ...(uuid ? { uuid } : null),
      name,
      description,
    }),

  /** POST same endpoint; `form_method: 'delete'` */
  delete: ({ id, uuid }) =>
    api.post('/api/productCategory/iformAction', {
      form_method: 'delete',
      ...(id != null && id !== '' ? { id } : {}),
      ...(uuid ? { uuid } : {}),
    }),
};

export const productApi = {
  /** GET /api/product/ilist — optional search `q`; paginate + per_page + page */
  ilist: ({ paginate = true, per_page = 10, page = 1, q } = {}) =>
    api.get('/api/product/ilist', {
      params: {
        paginate,
        per_page,
        page,
        ...(q != null && String(q).trim() !== '' ? { q: String(q).trim() } : {}),
      },
    }),

  /** `iformAction` save — e.g. name, category_id, selling_price, buying_price, reorder_level, quantity, registered_date */
  save: (payload) =>
    api.post('/api/product/iformAction', {
      form_method: 'save',
      ...payload,
    }),

  /** `iformAction` update — same core fields as `save` plus `id` (and optional `uuid`) */
  update: (payload) =>
    api.post('/api/product/iformAction', {
      form_method: 'update',
      ...payload,
    }),

  /** POST same endpoint; `form_method: 'delete'` */
  delete: ({ id, uuid }) =>
    api.post('/api/product/iformAction', {
      form_method: 'delete',
      ...(id != null ? { id } : {}),
      ...(uuid ? { uuid } : {}),
    }),
};

export const productBalanceApi = {
  /** GET /api/productBalance/ilist — paginated list; optional search `q` */
  ilist: ({ paginate = true, per_page = 10, page = 1, q } = {}) =>
    api.get('/api/productBalance/ilist', {
      params: {
        paginate,
        per_page,
        page,
        ...(q != null && String(q).trim() !== '' ? { q: String(q).trim() } : {}),
      },
    }),

  /** POST /api/productBalance/iformAction — e.g. form_method: save, product_id, balance */
  iformAction: (payload) =>
    api.post('/api/productBalance/iformAction', payload),

  /** Update existing balance row (same endpoint; include balance record `id`). */
  update: (payload) =>
    api.post('/api/productBalance/iformAction', {
      form_method: 'update',
      ...payload,
    }),

  save: (payload) =>
    api.post('/api/productBalance/iformAction', {
      form_method: 'save',
      ...payload,
    }),
};

export const expensesCategoryApi = {
  /** GET /api/expensesCategory/ilist — paginated list; optional search `q` */
  ilist: ({ paginate = true, per_page = 5, page = 1, q } = {}) =>
    api.get('/api/expensesCategory/ilist', {
      params: {
        paginate,
        per_page,
        page,
        ...(q != null && String(q).trim() !== '' ? { q: String(q).trim() } : {}),
      },
    }),

  save: ({ id, uuid, name, description }) =>
    api.post('/api/expensesCategory/iformAction', {
      form_method: 'save',
      ...(id ? { id } : null),
      ...(uuid ? { uuid } : null),
      name,
      description,
    }),

  delete: ({ id, uuid }) =>
    api.post('/api/expensesCategory/iformAction', {
      form_method: 'delete',
      ...(id != null && id !== '' ? { id } : {}),
      ...(uuid ? { uuid } : {}),
    }),
};

export const expensesApi = {
  /** GET /api/expenses/ilist — paginated list; optional search `q` */
  ilist: ({ paginate = true, per_page = 10, page = 1, q } = {}) =>
    api.get('/api/expenses/ilist', {
      params: {
        paginate,
        per_page,
        page,
        ...(q != null && String(q).trim() !== '' ? { q: String(q).trim() } : {}),
      },
    }),

  /** POST /api/expenses/iformAction — save / update payloads */
  iformAction: (payload) => api.post('/api/expenses/iformAction', payload),

  delete: ({ id }) =>
    api.post('/api/expenses/iformAction', {
      form_method: 'delete',
      id,
    }),
};

export const salesApi = {
  /** GET /api/sales/ilist — paginated list; optional search `q` */
  ilist: ({ paginate = true, per_page = 5, page = 1, q } = {}) =>
    api.get('/api/sales/ilist', {
      params: {
        paginate,
        per_page,
        page,
        ...(q != null && String(q).trim() !== '' ? { q: String(q).trim() } : {}),
      },
    }),

  /** POST /api/sales/iformAction — e.g. form_method: save, sales_items, amount, … */
  iformAction: (payload) =>
    api.post('/api/sales/iformAction', payload),
};

export const reportApi = {
  /**
   * GET /api/report/profitAndLoss?startDate=YYYY-MM-DD&endDate=YYYY-MM-DD
   * Response: { period, sales: { total, items[] }, buying: { total, items[], total_buying_price },
   * expenses: { total, items[] }, summary: { total_sales, total_buying_price, total_expenses,
   * total_profit, net_profit }, optional products[] }. Reports: first card uses
   * `summary.net_profit`; per-product table from `products`, else `sales.items`, else `buying.items`.
   */
  profitAndLoss: ({ startDate, endDate }) =>
    api.get('/api/report/profitAndLoss', {
      params: {
        startDate: String(startDate).trim(),
        endDate: String(endDate).trim(),
      },
    }),
};

